import { DataTableColDef, IconButton, Link, Menu, MenuItem } from '@chronosphereio/chrono-ui';
import { DotsVertical } from 'mdi-material-ui';
import { ReactNode, useMemo, useState } from 'react';
import { ContextLinkDefinition, validateUrlProtocol } from '@/core';
import { useInterpolateContextLinkFn } from '@/plugin-system/runtime/context-links';

export const LINKS_COLUMN_FIELD_IDENTIFIER = 'internal-links-column';

export const LinksColumnContent = ({ children }: { children: ReactNode }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setIsOpen(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setIsOpen(false);
  };

  return (
    <>
      <IconButton onClick={handleClick} aria-label="See more actions">
        <DotsVertical />
      </IconButton>
      <Menu open={isOpen} anchorEl={anchorEl} onClose={handleClose} onClick={handleClose}>
        {children}
      </Menu>
    </>
  );
};

export const addLinksColumn = (
  links: ContextLinkDefinition[],
  columns: DataTableColDef[],
  interpolateContextLink: ReturnType<typeof useInterpolateContextLinkFn>
) => {
  if (links && links.length > 0) {
    const linksColumn: DataTableColDef = {
      field: LINKS_COLUMN_FIELD_IDENTIFIER,
      headerName: '',
      width: 40,
      renderCell: (params) => {
        return (
          <LinksColumnContent>
            {links.map((link, index) => {
              const url = interpolateContextLink(link.spec.url, {
                labels: {
                  ...params.row,
                },
              });
              const safeUrl = validateUrlProtocol(url);

              // Disallowed protocols (e.g. javascript:) are dropped at the
              // sink: render the row as a disabled menu item so the disallowed
              // href cannot reach the DOM.
              if (safeUrl === null) {
                return (
                  <MenuItem key={index} disabled>
                    {link.spec.title}
                  </MenuItem>
                );
              }

              return (
                <Link key={index} href={safeUrl} target="_blank" rel="noreferrer">
                  <MenuItem>{link.spec.title}</MenuItem>
                </Link>
              );
            })}
          </LinksColumnContent>
        );
      },
      disableColumnMenu: true,
      disableReorder: true,
      sortable: false,
      pinnable: false,
      resizable: false,
      // The button should always fit and the expandable cell styling doesn't play nicely with it, so
      // disabling for this cell.
      disableExpandableCell: true,
    };

    return [...columns, linksColumn];
  }

  return columns;
};

export const useLinksColumn = (links: ContextLinkDefinition[] | undefined, columns: DataTableColDef[]) => {
  const interpolateContextLink = useInterpolateContextLinkFn();

  return useMemo(() => {
    if (links && links.length > 0) {
      return addLinksColumn(links, columns, interpolateContextLink);
    }

    return columns;
  }, [links, columns, interpolateContextLink]);
};
